/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { LIVESELL_URL, MILLISECOND, Views } from '../../constants';
import { AppStatus, DURATION_TIMER_STATE, SESSION_END_LAYOUT } from '../../constants/enums';
import { AdvancedSettings } from '../../context/types';
import layoutStates from '../../recoil/atoms/layout';
import { isDurationAlertDisplayedAtom, sessionEndModalAtom, sessionEndPageAtom } from '../../recoil/atoms/sessionModalData';
import { setOverlayTimerSelector, TIMER_OVERLAY_STATE } from "../../recoil/atoms/timer-overlay"
import { viewSelector } from '../../recoil/atoms/view';
import { wrapLinksInAnchorTags } from '../../util';
import { ConsoleLogger } from '../../util/logger';
import useConfig from '../useConfig';
import { ToastConfigProps, useCustomToast } from '../useToast';

const logger = new ConsoleLogger()
const LEAVE_DELAY_SEC = 4 * MILLISECOND// additional timeout for explicitly logout


// Sample response
// {
//   durationTimerState: 0,
//   advancedSettings : {
//     alertTitle: "Alert Title 2",
//     alertMessage: "Alert short message 2",
//     alertShowTime: "2400",
//     alertDuration: "720",
//     allowedDuration: "3300",
//     usedDuration: "965",
//     onscreenTimerDirection: 1
//   }
// }

type HandleRoomDurationRes = {
  durationTimerState: number,
  advancedSettings: AdvancedSettings
}

type AlertData = Pick<AdvancedSettings, 'alertTitle' | 'alertMessage' | 'alertShowTime' | 'alertDuration'>;

let alertShowTimeoutId = null
let durationExpiredTimeoutId = null
let fallbackLogoutTimeoutId = null

const useDurationTimeoutAlertMessage = () => {
  const [isDurationAlertDisplayed, setIsDurationAlertDisplayed] = useRecoilState(isDurationAlertDisplayedAtom)
  const config: ToastConfigProps = {
    toastOptions: {
      containerStyle: {
        margin: "0.1rem"
      }
    },
    titleStyles: {
      fontWeight: 700
    }
  }
  const showToastMessage = useCustomToast(config)

  const isDurationAlertDisplayedRef = useRef(isDurationAlertDisplayed)
  isDurationAlertDisplayedRef.current = isDurationAlertDisplayed

  const showAlertDurationMessage = useCallback(({alertTitle, alertMessage, alertDuration }: AlertData) => {
    
    // Display toast message only once
    if(!isDurationAlertDisplayedRef.current) {
      setIsDurationAlertDisplayed(true)
      
      // warp the link with <a> tag if link is present in message
      const formattedMessage = wrapLinksInAnchorTags(alertMessage) 
      showToastMessage(alertTitle, formattedMessage, { duration: alertDuration * MILLISECOND })
    }

  }, [])

  const startDurationAlertTimer = useCallback((alertData: AlertData, duration: number) => {
    logger.log("startDurationAlertTimer", duration)
    stopDurationAlertTimer()
    alertShowTimeoutId = setTimeout(() => {
      // usedDuration is between the alertDuration and showDuration
      showAlertDurationMessage(alertData)
    }, duration * MILLISECOND)
  }, [])

  const setDurationAlertTimer = useCallback((alertData: AlertData, usedDuration: number) => {
    const { alertShowTime, alertDuration  } = alertData

    if( usedDuration < alertShowTime ) {
      // -> If usedDuration is less then alertShowTime, then start the timer for showing alert message 
      const showAlertAt = alertShowTime - usedDuration
      startDurationAlertTimer(alertData, showAlertAt)

    } else if ( alertShowTime <= usedDuration && usedDuration <= alertShowTime + alertDuration ) {
      // -> If usedDuration is between then alertShowTime & alertDuration, then show alert Message
      showAlertDurationMessage(alertData)
    }

  }, [])

  const stopDurationAlertTimer = useCallback(() => {
    // -> Clear the timer
    alertShowTimeoutId && clearTimeout(alertShowTimeoutId)
  }, [])

  return { setDurationAlertTimer, stopDurationAlertTimer}
}

const useDurationExpireTimer = () => {
  const { t } = useTranslation()
  const setView = useSetRecoilState(viewSelector)
  const setSessionEndPage =  useSetRecoilState(sessionEndPageAtom)
  const setSessionEndModal = useSetRecoilState(sessionEndModalAtom)
  const showJoinAgainButtonOnByePage =  useSetRecoilState(layoutStates.showJoinAgainButtonOnByePage)
  const config = useConfig()

  const leaveRoom = useCallback(() => {
    window.MeetOnline._moClient?.disconnectGateway({
      code: AppStatus.ROOM_DURATION_TIMEOUT
    })
    if (config.postLoginRedirectUrl === "") {
      setView(Views.BYE)
    }
  }, [])

  const handleDurationExpired = useCallback((settings: AdvancedSettings, addDelayToLogout: boolean = false) => {
    logger.log("handleDurationExpired", settings)
    
    if(settings.sessionEndLayout === SESSION_END_LAYOUT.MODAL) {
      // -> Add session end modal data if layout is set to modal
      setSessionEndModal({ 
        sessionEndModalTitle: settings.sessionEndModalTitle ?? t("Session over"),
        sessionEndModalMessage: settings.sessionEndModalMessage ?? t("Please click below to leave"),
        sessionEndModalCloseButtonText: settings.sessionEndModalCloseButtonText ?? t("Go to LiveSell"),
        sessionEndModalCloseButtonLink: settings.sessionEndModalCloseButtonLink ?? LIVESELL_URL
      })
    } else {
      // -> Add session end page data if layout is set to page
      setSessionEndPage({
        sessionEndPageTitle: settings.sessionEndPageTitle,
        sessionEndPageMessage: settings.sessionEndPageMessage,
        sessionEndPageCloseButtonText:settings.sessionEndPageCloseButtonText,
        sessionEndPageCloseButtonLink: settings.sessionEndPageCloseButtonLink
      })
    }

    showJoinAgainButtonOnByePage(false)

    if(addDelayToLogout) {
      fallbackLogoutTimeoutId = setTimeout(leaveRoom, LEAVE_DELAY_SEC)
    } else {
      leaveRoom()
    }
  
  }, [])

  const startDurationExpireTimer = useCallback(({ allowedDuration, usedDuration, ...modalData}: AdvancedSettings) => {
    stopDurationExpireTimer()
    // set logout timer

    const remainingDuration = allowedDuration - usedDuration
    logger.log("Set Duration Expire Timer: ", remainingDuration)

    durationExpiredTimeoutId = setTimeout(() => {
      logger.log("Duration Expire Timer Executing")
      // add delay to logout, if server fail to send stop timer message
      handleDurationExpired(modalData, true)

    }, remainingDuration * MILLISECOND)

  }, [])

  const stopDurationExpireTimer = useCallback(() => {
    durationExpiredTimeoutId && clearTimeout(durationExpiredTimeoutId)
    fallbackLogoutTimeoutId && clearTimeout(fallbackLogoutTimeoutId)
  }, [])

  return { startDurationExpireTimer, stopDurationExpireTimer, handleDurationExpired }
}

const useRoomDurationTimeout = () => {
  
  
  const setTimerOverlay = useSetRecoilState(setOverlayTimerSelector)
  const { setDurationAlertTimer, stopDurationAlertTimer } = useDurationTimeoutAlertMessage() 
  const { startDurationExpireTimer, stopDurationExpireTimer, handleDurationExpired } = useDurationExpireTimer()

  // Function to handle server response
  const handleRoomDurationResponse = useCallback(({ durationTimerState, advancedSettings }: HandleRoomDurationRes) => {
    logger.log("handleRoomDurationResponse", durationTimerState, advancedSettings)
    
    const { onscreenTimerDirection, allowedDuration, usedDuration = 0 } = advancedSettings

    
    if (durationTimerState === DURATION_TIMER_STATE.START) {  
      // -> Start or resume the timer
      
      // 1. if onscreenTimerDirection has value,then show timer overlay
      if(onscreenTimerDirection) {
        setTimerOverlay({
          action: TIMER_OVERLAY_STATE.START,
          payload: { 
            allowedDuration: allowedDuration * MILLISECOND, 
            usedDuration: usedDuration * MILLISECOND,
            timerDirection: onscreenTimerDirection
          }
        })
      }

      // 2. Show alert message if alertShowTime is present. 
      if(advancedSettings.alertShowTime) {
        setDurationAlertTimer(advancedSettings, usedDuration)        
      }

      // 3. start duration expire timer for leaving meeting and showing session expire modal
      startDurationExpireTimer(advancedSettings)

    } else {
      // -> Pause the timer
      logger.log("Stop Duration Timer")
      // 1. if onscreenTimerDirection has value,then show timer overlay
     
      if(onscreenTimerDirection) {
        // Pause overlay timer & update timer with server time
        setTimerOverlay({
          action: TIMER_OVERLAY_STATE.PAUSE,
          payload: { 
            allowedDuration: allowedDuration * MILLISECOND, 
            usedDuration: usedDuration * MILLISECOND,
            timerDirection: onscreenTimerDirection
          }
        })
      }

      // Stop alert and sessionExpire Timer
      stopDurationAlertTimer()
      stopDurationExpireTimer()

      if(allowedDuration <= usedDuration) {
        // if usedDuration is more than allowedDuration, then logout the user
        handleDurationExpired(advancedSettings)
      }

    } 

  }, [])

  return { handleRoomDurationResponse }
}

export const clearRoomDurationSetTimeout = () => {
  alertShowTimeoutId && clearTimeout(alertShowTimeoutId)
  durationExpiredTimeoutId && clearTimeout(durationExpiredTimeoutId)
  fallbackLogoutTimeoutId && clearTimeout(fallbackLogoutTimeoutId)
}

export default useRoomDurationTimeout